import { createContext, useContext, type ReactNode } from 'react';
import { assert } from '~/lib/assert';
import type { DeviceFilters, DeviceFiltersState, FilterReducerProps } from './';
import { useDeviceFiltersReducer } from './reducer';

export type DeviceFiltersContextType = DeviceFiltersState & {
  clearFilters: () => void;
  reinitialize: (params: string) => void;
  setFilter: (
    field: {
      [K in keyof DeviceFilters]: { field: K; items: DeviceFilters[K]['items']; open?: boolean };
    }[keyof DeviceFilters],
  ) => void;
  toggleFilter: (field: keyof DeviceFilters) => void;
  togglePanel: () => void;
};

export const DeviceFiltersContext = createContext<DeviceFiltersContextType | undefined>(undefined);

export const useDeviceFilters = () => {
  const context = useContext(DeviceFiltersContext);
  assert(context !== undefined, 'useDeviceFilters must be used with a DeviceFiltersProvider');
  return context;
};

export interface DeviceFiltersProviderProps extends FilterReducerProps {
  children: ReactNode;
}

export const DeviceFiltersProvider = ({
  children,
  ...reducerProps
}: DeviceFiltersProviderProps) => {
  const [state, dispatch] = useDeviceFiltersReducer(reducerProps);

  return (
    <DeviceFiltersContext.Provider
      value={{
        ...state,
        clearFilters: () => dispatch({ type: 'CLEAR_FILTERS' }),
        reinitialize: (params: string) => dispatch({ type: 'REINITIALIZE', params }),
        setFilter: (value) => dispatch({ type: 'SET_FILTER', ...value }),
        toggleFilter: (field) => dispatch({ type: 'TOGGLE_FILTER', field }),
        togglePanel: () => dispatch({ type: 'TOGGLE_PANEL' }),
      }}
    >
      {children}
    </DeviceFiltersContext.Provider>
  );
};
